import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import {
  HubHandlerMethod,
  HubHandlerSource,
  HubHandlerSourceAngular,
  HubHandlerSourceAngularJS,
  HubUserContextInfo,
} from '@models/system/signalR';
import { NotifyNewVersionComponent } from '@shared/components/notifications/notify-version';
import { APP_BUILD_NUMBER, APP_VERSION, AUTH_TOKEN } from '@shared/consts';
import { environment } from 'src/environments/environment';
import { EnvironmentConfigService } from './environment-config.service';
import { NotificationService } from './notification.service';

@Injectable({
  providedIn: 'root',
})
export class SignalRService implements OnDestroy {
  private readonly notificationService = inject(NotificationService);
  private readonly environmentConfigService = inject(EnvironmentConfigService);
  private readonly ngZone = inject(NgZone);

  private connection: signalR.HubConnection;
  private handlers: Record<HubHandlerSource, Record<string, HubHandlerMethod>> = this.emptyHandlers();
  private lastUserContextInfo: HubUserContextInfo;
  private debug = false;

  constructor() {
    if (localStorage.getItem('SignalRDebug') === 'true') {
      this.debug = true;
    }
  }

  ngOnDestroy() {
    this.connection?.stop();
    this.handlers = this.emptyHandlers();
    this.lastUserContextInfo = undefined;
  }

  async refresh(hubUserContextInfo: HubUserContextInfo) {
    await this.connect();
    await this.refreshConnectionInfo(hubUserContextInfo);
  }

  registerHandler(source: HubHandlerSource, methodName: string, method: HubHandlerMethod) {
    // multiple handlers can be registered but only one per source and method name
    const handlerIsAttached = Object.prototype.hasOwnProperty.call(this.handlers[source], methodName);

    // If the handler is already attached, we don't need to do anything further
    if (handlerIsAttached) {
      return;
    }

    // keep track of the handler
    this.handlers[source][methodName] = method;

    if (this.connection) {
      if (this.debug) {
        console.log(`Hub registering handler [${source}][${methodName}] after creating a connection`);
      }
      this.attachHandler(source, methodName, method);
    } else {
      if (this.debug) {
        console.log(`Hub registering handler [${source}][${methodName}] before creating a connection`);
      }
    }
  }

  private emptyHandlers(): Record<HubHandlerSource, Record<string, HubHandlerMethod>> {
    return { [HubHandlerSourceAngularJS]: {}, [HubHandlerSourceAngular]: {} };
  }

  private async refreshConnectionInfo(hubUserContextInfo: HubUserContextInfo) {
    if (!hubUserContextInfo || !this.isConnected()) {
      return;
    }

    if (
      hubUserContextInfo.context?.contract &&
      (!hubUserContextInfo.context?.program || !hubUserContextInfo.context?.project)
    ) {
      // skip this case, as this method will hit again with full context
      return;
    }

    if (
      hubUserContextInfo.context?.program &&
      (hubUserContextInfo.context.program as unknown as Record<string, unknown>)['projects']
    ) {
      delete (hubUserContextInfo.context.program as unknown as Record<string, unknown>)['projects'];
    }
    if (
      hubUserContextInfo.context?.project &&
      (hubUserContextInfo.context.project as unknown as Record<string, unknown>)['contracts']
    ) {
      delete (hubUserContextInfo.context.project as unknown as Record<string, unknown>)['contracts'];
    }
    this.lastUserContextInfo = hubUserContextInfo;

    if (this.debug) {
      console.log('Hub refresh connection to', this.lastUserContextInfo);
    }
    await this.connection.invoke('RefreshConnectionInfo', this.lastUserContextInfo);
  }

  private isConnected(): boolean {
    return this.connection && this.connection.state === signalR.HubConnectionState.Connected;
  }

  private async connect() {
    // If the connection is already established or in the process of connecting, do nothing
    if (
      this.connection &&
      (this.connection.state === signalR.HubConnectionState.Connected ||
        this.connection.state === signalR.HubConnectionState.Connecting ||
        this.connection.state === signalR.HubConnectionState.Reconnecting)
    ) {
      return;
    }

    if (this.debug) {
      console.log('Hub connecting...');
    }
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(environment.baseApiUrl + 'hub', {
        accessTokenFactory: () => localStorage.getItem(AUTH_TOKEN),
        logger: this.debug ? signalR.LogLevel.Debug : signalR.LogLevel.None,
      })
      // Retry intervals: 0ms, 2s, 10s, 30s, 2min, 10min, 30min
      .withAutomaticReconnect([0, 2000, 10000, 30000, 120000, 600000, 1800000])
      .build();

    // Attach all handlers
    Object.entries(this.handlers).forEach(([source, sourceHandlers]) => {
      Object.entries(sourceHandlers).forEach(([methodName, method]) => {
        this.attachHandler(source, methodName, method);
      });
    });

    this.connection.onclose(async () => {
      if (this.debug) {
        console.log('Hub connection closed. Trying to reconnect...');
      }
      await this.start();
    });

    this.connection.onreconnecting(error => {
      if (this.debug) {
        console.error('Hub connection lost. Attempting to reconnect...', error);
      }
    });

    this.connection.onreconnected(async connectionId => {
      if (this.debug) {
        console.log('Hub reconnected with connection id', connectionId);
      }
      this.refreshConnectionInfo(this.lastUserContextInfo);

      // Check for new app version upon connection start
      this.environmentConfigService
        .readConfiguration()
        .subscribe(({ appVersion, appBuildNumber }) => this.handleNewAppVersion(appVersion, appBuildNumber));
    });

    await this.start();

    // After start, always refresh connection info
    this.refreshConnectionInfo(this.lastUserContextInfo);
  }

  private attachHandler(source: HubHandlerSource, methodName: string, method: HubHandlerMethod) {
    if (this.debug) {
      console.log(`Hub attaching handler [${source}][${methodName}]`);
    }
    // SignalR callbacks fire outside Angular's zone — run inside so signals and services trigger change detection
    this.connection.on(methodName, (...args) => this.ngZone.run(() => method(...args)));
  }

  private async start() {
    try {
      await this.connection.start();
      if (this.debug) {
        console.log('Hub connected with connection id', this.connection.connectionId);
      }
    } catch (err) {
      if (this.debug) {
        console.error(err);
      }
    }
  }

  private handleNewAppVersion(appVersion: string, appBuildNumber: string) {
    // avoid comparing undefined/ null values
    appVersion = appVersion || '';
    appBuildNumber = appBuildNumber || '';

    // get local stored version
    const localAppVersion = localStorage.getItem(APP_VERSION) ?? '';
    const localAppBuildNumber = localStorage.getItem(APP_BUILD_NUMBER) ?? '';

    // check if a local version exists
    const existsLocalVersion = !!localAppVersion;

    // compare versions
    const sameVersion = appVersion === localAppVersion && appBuildNumber === localAppBuildNumber;
    const msgVersionComparison = `Previous: ${localAppVersion} ${localAppBuildNumber}, New: ${appVersion} ${appBuildNumber}`;

    // store the new version info if not stored yet
    if (!existsLocalVersion || !sameVersion) {
      if (!existsLocalVersion) {
        if (this.debug) {
          console.log('No local app version stored yet. Storing version info:', appVersion, appBuildNumber);
        }
      } else if (!sameVersion) {
        if (this.debug) {
          console.log(`App version changed. ${msgVersionComparison}`);
        }
      }

      if (this.debug) {
        console.log('Storing new app version info', appVersion, appBuildNumber);
      }
      localStorage.setItem(APP_VERSION, appVersion);
      localStorage.setItem(APP_BUILD_NUMBER, appBuildNumber);
    }

    // if no local version existed, no need to notify
    if (!existsLocalVersion) {
      if (this.debug) {
        console.log(`No local app version was found, skipping notification. ${msgVersionComparison}`);
      }
    }

    // if same version, no need to notify
    if (sameVersion) {
      if (this.debug) {
        console.log(`Same version was found, skipping notification. ${msgVersionComparison}`);
      }
    }

    // notify user about the new version
    if (existsLocalVersion && !sameVersion) {
      // prepare version message for notification
      const buildNumberMessage = !environment.production && appBuildNumber ? ` (Build: ${appBuildNumber})` : '';
      const versionMessage = `, v${appVersion}${buildNumberMessage}`;

      if (this.debug) {
        console.log('Notifying user. New app version detected', versionMessage);
      }

      this.notificationService
        .snackBarMessageFromComponent(NotifyNewVersionComponent)
        .afterDismissed()
        .subscribe(() => {
          // refresh the browser to load the new version
          window.location.reload();
        });
    }
  }
}
